import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SectionWrapper from '../components/SectionWrapper';
import { Target, BookOpen, TrendingUp, Award, Users, Calendar } from 'lucide-react';
import { usePageContent, usePrograms, useFeatures, useDifferentiators } from '../hooks/usePageContent';
import { useSections } from '../hooks/useSections';
import axios from 'axios';
import { API } from '../App';

const HomePage = () => {
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  
  // Fetch sections configuration (visibility and ordering)
  const { sections, loading: sectionsLoading } = useSections(false); // Get all sections including hidden
  
  // Fetch page content from database
  const { getContent: getHeroContent } = usePageContent('home', 'hero');
  const { getContent: getGuidingContent } = usePageContent('home', 'guiding-principles');
  const { getContent: getNcertVideoContent } = usePageContent('home', 'ncert-showcase');
  const { getContent: getNcertPosterContent } = usePageContent('home', 'ncert-poster');
  const { programs } = usePrograms();
  const { features } = useFeatures();
  const { differentiators } = useDifferentiators();
  
  // Helper function to check if a section is visible
  const isSectionVisible = (sectionType) => {
    if (sectionsLoading || !sections || sections.length === 0) {
      // Default to true while loading or if sections not configured
      return true;
    }
    const section = sections.find(s => s.type === sectionType);
    return section ? section.is_visible : true;
  };
  
  // Get section display order
  const getSectionOrder = (sectionType) => {
    if (!sections || sections.length === 0) return 999;
    const section = sections.find(s => s.type === sectionType);
    return section ? section.display_order : 999;
  };
  
  // Load site settings for hero video configuration
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSiteSettings(res.data);
      } catch (e) {
        console.error('Failed to load settings', e);
      }
    };
    fetchSettings();
  }, []);
  
  // YouTube URL detection and video ID extraction
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    
    // Match various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  };
  
  // Icon mapping helper
  const getIconComponent = (iconName) => {
    const icons = {
      Target,
      BookOpen,
      TrendingUp,
      Award,
      Users,
      Calendar
    };
    return icons[iconName] || BookOpen;
  };

  return (
    <div>
      <Navbar />
      
      {/* Hero Section */}
      <SectionWrapper sectionType="hero" sections={sections} sectionsLoading={sectionsLoading}>
      <section className="animate-fade-in hero-section-responsive" style={{ 
        paddingTop: '0',
        paddingBottom: '0',
        minHeight: '100vh',
        height: '100vh',
        width: '100%',
        margin: '0',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        position: 'relative', 
        overflow: 'hidden'
      }}>
        {/* Background Video/Image Layer */}
        {siteSettings?.hero_background_type === 'video' && siteSettings?.hero_video_url ? (
          (() => {
            const youtubeId = getYouTubeVideoId(siteSettings.hero_video_url);
            
            if (youtubeId) {
              // YouTube embed
              const autoplay = siteSettings.hero_video_autoplay ?? true;
              const muted = siteSettings.hero_video_muted ?? true;
              const loop = siteSettings.hero_video_loop ?? true;
              
              const params = new URLSearchParams({
                autoplay: autoplay ? '1' : '0',
                mute: muted ? '1' : '0',
                loop: loop ? '1' : '0',
                playlist: youtubeId, // Required for loop to work
                controls: '0',
                showinfo: '0',
                rel: '0',
                modestbranding: '1',
                playsinline: '1',
                enablejsapi: '1'
              });
              
              return (
                <iframe
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '100vw',
                    height: '56.25vw', // 16:9 ratio
                    minHeight: '100vh',
                    minWidth: '177.77vh', // 16:9 ratio
                    transform: 'translate(-50%, -50%)',
                    zIndex: 0,
                    pointerEvents: 'none'
                  }}
                  src={`https://www.youtube.com/embed/${youtubeId}?${params.toString()}`}
                  title="Hero Background Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              );
            } else {
              // Regular HTML5 video
              return (
                <video
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: 0
                  }}
                  src={siteSettings.hero_video_url}
                  poster={siteSettings.hero_video_poster || undefined}
                  autoPlay={siteSettings.hero_video_autoplay ?? true}
                  muted={siteSettings.hero_video_muted ?? true}
                  loop={siteSettings.hero_video_loop ?? true}
                  playsInline
                  controls={false}
                />
              );
            }
          })()
        ) : (
          // Static background image (default)
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_academy-admin-2/artifacts/noos7878_b4e86226e4f40223970df34ac456574d.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            zIndex: 0
          }}></div>
        )}
        {/* Elegant Gradient Overlay with bottom fade to white */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(26, 32, 44, 0.5) 0%, rgba(45, 55, 72, 0.4) 30%, rgba(113, 128, 150, 0.3) 60%, rgba(237, 242, 247, 0.7) 85%, rgba(255, 255, 255, 0.95) 100%)',
          zIndex: 1
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 2, maxWidth: '1200px', padding: '0 2rem' }}>
          <div style={{ 
            textAlign: 'center', 
            maxWidth: '950px', 
            margin: '0 auto',
            animation: 'fadeInUp 1s ease'
          }}>
            {/* Main Heading with improved styling */}
            <h1 style={{ 
              fontSize: 'clamp(2rem, 5vw, 4rem)', 
              marginBottom: '1.5rem', 
              fontWeight: 800,
              lineHeight: 1.3,
              color: 'white',
              textAlign: 'center',
              textShadow: '2px 4px 12px rgba(0, 0, 0, 0.6), 0 2px 4px rgba(0, 0, 0, 0.4)',
              letterSpacing: '-0.02em'
            }}>
              Unlock Your Potential.<br />
              Serve the Nation. 🇮🇳
            </h1>
            
            {/* Refined Subtitle */}
            <p className="hero-subtitle" style={{ 
              fontSize: 'clamp(1rem, 1.8vw, 1.3rem)', 
              color: '#ffffff', 
              marginBottom: '3rem', 
              lineHeight: 1.7,
              textShadow: '2px 2px 8px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 0, 0, 0.7)',
              fontWeight: 500,
              textAlign: 'center',
              maxWidth: '900px',
              margin: '0 auto 3rem auto'
            }}>
              Master <strong style={{ color: '#00FFFF', fontWeight: 700 }}>UPSC & KPSC</strong> with elite mentorship, dynamic test series, and a roadmap to success.
            </p>
            
            {/* Enhanced CTA Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'center', 
              flexWrap: 'wrap',
              animation: 'fadeInUp 1s ease 0.2s backwards'
            }}>
              <Link 
                to="/ncert-mastery" 
                className="btn-primary" 
                data-testid="explore-btn" 
                style={{ 
                  fontSize: '0.95rem', 
                  padding: '0.75rem 2rem',
                  background: '#FF8C38',
                  color: 'white',
                  border: 'none',
                  boxShadow: '0 4px 20px rgba(255, 140, 56, 0.4)',
                  fontWeight: 600,
                  borderRadius: '50px',
                  transition: 'all 0.3s ease',
                  textTransform: 'none',
                  letterSpacing: '0px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                ⚡ Explore Learning Paths
              </Link>
              <a 
                href="https://forms.gle/5mjmuxov7LaYCHTU6" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-secondary" 
                data-testid="enroll-btn" 
                style={{ 
                  fontSize: '0.95rem', 
                  padding: '0.75rem 2rem',
                  background: 'transparent',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  border: '2px solid white',
                  fontWeight: 600,
                  borderRadius: '50px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.3s ease',
                  textTransform: 'none',
                  letterSpacing: '0px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                ✍️ Enroll Now
              </a>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div style={{
          position: 'absolute',
          bottom: '2rem',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
          animation: 'bounce 2s ease-in-out infinite'
        }}>
          <div style={{
            width: '30px',
            height: '50px',
            border: '2px solid rgba(255, 255, 255, 0.5)',
            borderRadius: '20px',
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '8px'
          }}>
            <div style={{
              width: '4px',
              height: '8px',
              background: 'white',
              borderRadius: '2px',
              animation: 'scroll 2s ease-in-out infinite'
            }}></div>
          </div>
        </div>
      </section>
      </SectionWrapper>

      {/* Key Highlights - Why Choose Us */}
      <SectionWrapper sectionType="why-choose" sections={sections} sectionsLoading={sectionsLoading}>
      <section className="py-8">
        <div className="container">
          <div className="why-choose-container" style={{
            border: '2px solid #E5E7EB',
            borderRadius: '16px',
            padding: '2rem 1.5rem',
            background: 'white'
          }}>
            <h2 className="text-center" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', marginBottom: '2rem', color: 'var(--primary)', fontWeight: 700 }}>Why Choose Adhva Academy?</h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(4, 1fr)', 
              gap: '1.5rem',
              maxWidth: '1400px',
              margin: '0 auto'
            }} className="why-choose-grid">
              {/* Expert Mentorship */}
              <div 
                className="card text-center why-choose-card" 
                data-testid="highlight-1" 
                style={{ 
                  transition: 'all 0.3s ease',
                  padding: '1.25rem 0.875rem',
                  border: '2px solid transparent',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.border = '3px solid var(--accent)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 140, 56, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.border = '2px solid transparent';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ 
                  width: '45px', 
                  height: '45px', 
                  borderRadius: '50%', 
                  background: '#FFF3E6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.75rem',
                  color: 'var(--accent)'
                }} className="why-choose-icon-bg">
                  <Target size={22} strokeWidth={2} />
                </div>
                <h3 style={{ 
                  marginBottom: '0.5rem', 
                  fontSize: '0.95rem',
                  fontWeight: 700
                }} className="why-choose-title">Expert Mentorship</h3>
                <p style={{ 
                  fontSize: '0.75rem',
                  lineHeight: 1.4
                }} className="why-choose-text">
                  Personalized guidance from seasoned mentors & ex-civil servants.
                </p>
              </div>

              {/* Comprehensive Test Series */}
              <div 
                className="card text-center why-choose-card" 
                data-testid="highlight-2" 
                style={{ 
                  transition: 'all 0.3s ease',
                  padding: '1.25rem 0.875rem',
                  border: '2px solid transparent',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.border = '3px solid var(--accent)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 140, 56, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.border = '2px solid transparent';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ 
                  width: '45px', 
                  height: '45px', 
                  borderRadius: '50%', 
                  background: '#FFF3E6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.75rem',
                  color: 'var(--accent)'
                }} className="why-choose-icon-bg">
                  <BookOpen size={22} strokeWidth={2} />
                </div>
                <h3 style={{ 
                  marginBottom: '0.5rem', 
                  fontSize: '0.95rem',
                  fontWeight: 700
                }} className="why-choose-title">Comprehensive Test Series</h3>
                <p style={{ 
                  fontSize: '0.75rem',
                  lineHeight: 1.4
                }} className="why-choose-text">
                  Regular tests mirroring exam patterns with detailed feedback.
                </p>
              </div>

              {/* NCERT Foundation */}
              <div 
                className="card text-center why-choose-card" 
                data-testid="highlight-3" 
                style={{ 
                  transition: 'all 0.3s ease',
                  padding: '1.25rem 0.875rem',
                  border: '2px solid transparent',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.border = '3px solid var(--accent)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 140, 56, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.border = '2px solid transparent';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ 
                  width: '45px', 
                  height: '45px', 
                  borderRadius: '50%', 
                  background: '#FFF3E6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.75rem',
                  color: 'var(--accent)'
                }} className="why-choose-icon-bg">
                  <Award size={22} strokeWidth={2} />
                </div>
                <h3 style={{ 
                  marginBottom: '0.5rem', 
                  fontSize: '0.95rem',
                  fontWeight: 700
                }} className="why-choose-title">NCERT Foundation</h3>
                <p style={{ 
                  fontSize: '0.75rem',
                  lineHeight: 1.4
                }} className="why-choose-text">
                  Strong fundamental clarity with our specialized NCERT modules.
                </p>
              </div>

              {/* Dynamic Community */}
              <div 
                className="card text-center why-choose-card" 
                data-testid="highlight-4" 
                style={{ 
                  transition: 'all 0.3s ease',
                  padding: '1.25rem 0.875rem',
                  border: '2px solid transparent',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.border = '3px solid var(--accent)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 140, 56, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.border = '2px solid transparent';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ 
                  width: '45px', 
                  height: '45px', 
                  borderRadius: '50%', 
                  background: '#FFF3E6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.75rem',
                  color: 'var(--accent)'
                }} className="why-choose-icon-bg">
                  <Users size={22} strokeWidth={2} />
                </div>
                <h3 style={{ 
                  marginBottom: '0.5rem', 
                  fontSize: '0.95rem',
                  fontWeight: 700
                }} className="why-choose-title">Dynamic Community</h3>
                <p style={{ 
                  fontSize: '0.75rem',
                  lineHeight: 1.4
                }} className="why-choose-text">
                  Learn and grow with a vibrant community of aspirants.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Add media query for responsive layout */}
        <style>{`
          @media (max-width: 1200px) {
            .why-choose-card {
              grid-column: span 2 !important;
            }
          }
          @media (max-width: 768px) {
            .why-choose-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            .why-choose-card {
              grid-column: span 1 !important;
            }
          }
        `}</style>
      </section>
      </SectionWrapper>

      {/* Adhva Prelims Path 2026 Section */}
      <SectionWrapper sectionType="prelims-path-2026" sections={sections} sectionsLoading={sectionsLoading}>
      <section className="py-8 prelims-section" style={{ 
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '3rem', 
            alignItems: 'center',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {/* Left Content */}
            <div style={{ padding: '2rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '0.5rem 1.5rem',
                  background: 'rgba(59, 130, 246, 0.2)',
                  color: '#60a5fa',
                  borderRadius: '50px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  border: '1px solid rgba(59, 130, 246, 0.3)'
                }}>
                  🎯 UPSC PRELIMS 2026
                </span>
              </div>
              
              <h2 style={{ 
                fontSize: 'clamp(2rem, 4vw, 3rem)', 
                marginBottom: '1.5rem', 
                color: 'white',
                fontWeight: 800,
                lineHeight: 1.2
              }}>
                Adhva Prelims Path 2026
              </h2>
              
              <p style={{ 
                fontSize: '1.125rem', 
                color: 'rgba(255, 255, 255, 0.9)', 
                marginBottom: '2rem',
                lineHeight: 1.7
              }}>
                Everything you need to clear Prelims in ONE complete program. Stop guessing. Start preparing with our comprehensive test series designed for UPSC 2026.
              </p>

              {/* Key Features */}
              <div style={{ marginBottom: '2rem' }}>
                {[
                  { icon: '📚', text: '45 Comprehensive Tests Across 4 Rounds' },
                  { icon: '🎯', text: 'Foundation to Final Mocks - Complete Coverage' },
                  { icon: '📊', text: 'PYQ-Driven High-Yield Advanced Tests' },
                  { icon: '✅', text: 'Structured Roadmap to Success' }
                ].map((feature, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1rem',
                    color: 'white',
                    fontSize: '1rem'
                  }}>
                    <span style={{ fontSize: '1.5rem' }}>{feature.icon}</span>
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <Link 
                  to="/adhva-prelims-path-2026" 
                  className="btn-primary"
                  style={{
                    background: '#3b82f6',
                    color: 'white',
                    padding: '0.875rem 2rem',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.3s',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                    border: 'none'
                  }}
                >
                  Explore Program →
                </Link>
                <a 
                  href="https://forms.gle/LJjeLHtjCNe5N2z3A" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    padding: '0.875rem 2rem',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.3s',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  Enroll Now 📝
                </a>
              </div>
            </div>

            {/* Right Visual */}
            <div style={{ 
              padding: '2rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                padding: '2.5rem',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                maxWidth: '500px',
                width: '100%'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(96, 165, 250, 0.1) 100%)',
                  borderRadius: '16px',
                  padding: '2rem',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '5rem',
                    marginBottom: '1rem',
                    filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                  }}>
                    🎯
                  </div>
                  <h3 className="hero-title-mobile" style={{
                    color: 'white',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    marginBottom: '1rem'
                  }}>
                    Your Path to Success
                  </h3>
                  <p className="hero-subtitle-mobile" style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '1rem',
                    lineHeight: 1.6
                  }}>
                    Join thousands of aspirants preparing for UPSC Prelims 2026
                  </p>
                  
                  {/* Stats */}
                  <div className="feature-grid-mobile" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '1rem',
                    marginTop: '2rem'
                  }}>
                    {[
                      { number: '45+', label: 'Tests' },
                      { number: '4', label: 'Rounds' },
                      { number: '100%', label: 'Coverage' },
                      { number: '₹3999', label: 'Early Join Offer', originalPrice: '₹4999', isPrice: true }
                    ].map((stat, idx) => (
                      <div key={idx} className="feature-box-mobile" style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        padding: '1rem',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        {stat.isPrice ? (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                              <div className="feature-number-mobile" style={{
                                color: '#60a5fa',
                                fontSize: '1.5rem',
                                fontWeight: 800
                              }}>
                                {stat.number}
                              </div>
                              {stat.originalPrice && (
                                <span style={{
                                  color: 'rgba(255, 255, 255, 0.5)',
                                  fontSize: '1rem',
                                  textDecoration: 'line-through',
                                  fontWeight: 600
                                }}>
                                  {stat.originalPrice}
                                </span>
                              )}
                            </div>
                            <div className="feature-label-mobile" style={{
                              color: 'rgba(255, 255, 255, 0.7)',
                              fontSize: '0.875rem'
                            }}>
                              {stat.label}
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="feature-number-mobile" style={{
                              color: '#60a5fa',
                              fontSize: '1.5rem',
                              fontWeight: 800,
                              marginBottom: '0.25rem'
                            }}>
                              {stat.number}
                            </div>
                            <div className="feature-label-mobile" style={{
                              color: 'rgba(255, 255, 255, 0.7)',
                              fontSize: '0.875rem'
                            }}>
                              {stat.label}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </SectionWrapper>

      {/* Adhva Monthly Current Affairs Section */}
      <SectionWrapper sectionType="monthly-current-affairs" sections={sections} sectionsLoading={sectionsLoading}>
      <section className="py-8 current-affairs-section" style={{ 
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(234, 179, 8, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(234, 179, 8, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '3rem', 
            alignItems: 'center',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {/* Left Visual */}
            <div className="current-affairs-visual" style={{ 
              padding: '2rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              order: 2
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                padding: '2.5rem',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                maxWidth: '500px',
                width: '100%'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.1) 0%, rgba(250, 204, 21, 0.1) 100%)',
                  borderRadius: '16px',
                  padding: '2rem',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '5rem',
                    marginBottom: '1rem',
                    filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                  }}>
                    📅
                  </div>
                  <h3 className="hero-title-mobile" style={{
                    color: 'white',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    marginBottom: '1rem'
                  }}>
                    Stay Updated
                  </h3>
                  <p className="hero-subtitle-mobile" style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '1rem',
                    lineHeight: 1.6
                  }}>
                    Master current affairs with our comprehensive monthly test series
                  </p>
                  
                  {/* Stats */}
                  <div className="feature-grid-mobile" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '1rem',
                    marginTop: '2rem'
                  }}>
                    {[
                      { number: '12', label: 'Months' },
                      { number: '100%', label: 'Current' },
                      { number: '24/7', label: 'Access' }
                    ].map((stat, idx) => (
                      <div key={idx} className="feature-box-mobile" style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        padding: '1rem',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <div className="feature-number-mobile" style={{
                          color: '#facc15',
                          fontSize: '1.5rem',
                          fontWeight: 800,
                          marginBottom: '0.25rem'
                        }}>
                          {stat.number}
                        </div>
                        <div className="feature-label-mobile" style={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.875rem'
                        }}>
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content */}
            <div className="current-affairs-content" style={{ padding: '2rem', order: 1 }}>
              <div style={{ marginBottom: '1rem' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '0.5rem 1.5rem',
                  background: 'rgba(234, 179, 8, 0.2)',
                  color: '#facc15',
                  borderRadius: '50px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  border: '1px solid rgba(234, 179, 8, 0.3)'
                }}>
                  📰 CURRENT AFFAIRS
                </span>
              </div>
              
              <h2 style={{ 
                fontSize: 'clamp(2rem, 4vw, 3rem)', 
                marginBottom: '1.5rem', 
                color: 'white',
                fontWeight: 800,
                lineHeight: 1.2
              }}>
                Adhva Monthly Current Affairs
              </h2>
              
              <p style={{ 
                fontSize: '1.125rem', 
                color: 'rgba(255, 255, 255, 0.9)', 
                marginBottom: '2rem',
                lineHeight: 1.7
              }}>
                Master UPSC Current Affairs with our comprehensive monthly test series. Stop worrying and start testing! Stay ahead with the most relevant and up-to-date content.
              </p>

              {/* Key Features */}
              <div style={{ marginBottom: '2rem' }}>
                {[
                  { icon: '📅', text: 'Monthly Test Series - Stay Current' },
                  { icon: '🎯', text: 'Comprehensive Coverage of Important Events' },
                  { icon: '📊', text: 'Detailed Analysis & Performance Tracking' },
                  { icon: '✅', text: 'Expert-Curated Questions & Explanations' }
                ].map((feature, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1rem',
                    color: 'white',
                    fontSize: '1rem'
                  }}>
                    <span style={{ fontSize: '1.5rem' }}>{feature.icon}</span>
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <Link 
                  to="/adhva-monthly-current-affairs" 
                  className="btn-primary"
                  style={{
                    background: '#eab308',
                    color: '#111827',
                    padding: '0.875rem 2rem',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.3s',
                    boxShadow: '0 4px 12px rgba(234, 179, 8, 0.3)',
                    border: 'none'
                  }}
                >
                  Explore Program →
                </Link>
                <a 
                  href="https://forms.gle/5mjmuxov7LaYCHTU6" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    padding: '0.875rem 2rem',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.3s',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  Get Started 📝
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Responsive Order Fix */}
        <style>{`
          @media (max-width: 768px) {
            .current-affairs-content {
              order: 1 !important;
            }
            .current-affairs-visual {
              order: 2 !important;
            }
          }
        `}</style>
      </section>
      </SectionWrapper>

      {/* Adhva Vibes PSI Section */}
      <SectionWrapper sectionType="psi-vibes" sections={sections} sectionsLoading={sectionsLoading}>
      <section className="py-8 psi-section" style={{ 
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)',
          pointerEvents: 'none'
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '3rem', 
            alignItems: 'center',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {/* Left Content */}
            <div style={{ padding: '2rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '0.5rem 1.5rem',
                  background: 'rgba(255, 140, 56, 0.2)',
                  color: '#FFB570',
                  borderRadius: '50px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  border: '1px solid rgba(255, 140, 56, 0.3)'
                }}>
                  🚔 KARNATAKA PSI EXAM
                </span>
              </div>
              
              <h2 style={{ 
                fontSize: 'clamp(2rem, 4vw, 3rem)', 
                marginBottom: '1.5rem', 
                color: 'white',
                fontWeight: 800,
                lineHeight: 1.2
              }}>
                Adhva Vibes: The PSI Journey
              </h2>
              
              <p style={{ 
                fontSize: '1.125rem', 
                color: 'rgba(255, 255, 255, 0.9)', 
                marginBottom: '2rem',
                lineHeight: 1.7
              }}>
                Your complete roadmap to ace the Karnataka PSI exam. From comprehensive test series to expert guidance, we've designed everything you need to earn that uniform.
              </p>

              {/* Key Features */}
              <div style={{ marginBottom: '2rem' }}>
                {[
                  { icon: '✅', text: '37 Comprehensive Tests (Paper 1 & 2)' },
                  { icon: '🎯', text: 'Data-Driven Strategy & Analysis' },
                  { icon: '👨‍🏫', text: 'Expert Feedback & Mentorship' },
                  { icon: '📊', text: 'All-Karnataka Ranking System' }
                ].map((feature, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1rem',
                    color: 'white',
                    fontSize: '1rem'
                  }}>
                    <span style={{ fontSize: '1.5rem' }}>{feature.icon}</span>
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <Link 
                  to="/adhva-vibes-psi" 
                  className="btn-primary"
                  style={{
                    background: '#FF8C38',
                    color: 'white',
                    padding: '0.875rem 2rem',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.3s',
                    boxShadow: '0 4px 12px rgba(255, 140, 56, 0.3)',
                    border: 'none'
                  }}
                >
                  Explore Programs →
                </Link>
                <a 
                  href="https://forms.gle/5mjmuxov7LaYCHTU6" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    padding: '0.875rem 2rem',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.3s',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  Get Sample Test 📄
                </a>
              </div>
            </div>

            {/* Right Visual */}
            <div style={{ 
              padding: '2rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                padding: '2.5rem',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                maxWidth: '500px',
                width: '100%'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(255, 140, 56, 0.1) 0%, rgba(255, 181, 112, 0.1) 100%)',
                  borderRadius: '16px',
                  padding: '2rem',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '5rem',
                    marginBottom: '1rem',
                    filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                  }}>
                    🚔
                  </div>
                  <h3 className="hero-title-mobile" style={{
                    color: 'white',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    marginBottom: '1rem'
                  }}>
                    Achieve Your Dream
                  </h3>
                  <p className="hero-subtitle-mobile" style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '1rem',
                    lineHeight: 1.6
                  }}>
                    Join hundreds of aspirants on their journey to becoming Police Sub-Inspectors
                  </p>
                  
                  {/* Stats */}
                  <div className="feature-grid-mobile" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '1rem',
                    marginTop: '2rem'
                  }}>
                    {[
                      { number: '37+', label: 'Tests' },
                      { number: '2', label: 'Papers' },
                      { number: '100%', label: 'Focused' },
                      { number: '24/7', label: 'Support' }
                    ].map((stat, idx) => (
                      <div key={idx} className="feature-box-mobile" style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        padding: '1rem',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <div className="feature-number-mobile" style={{
                          color: '#FFB570',
                          fontSize: '1.5rem',
                          fontWeight: 800,
                          marginBottom: '0.25rem'
                        }}>
                          {stat.number}
                        </div>
                        <div className="feature-label-mobile" style={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.875rem'
                        }}>
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
        `}</style>
      </section>
      </SectionWrapper>

      {/* NCERT Mastery Program Showcase */}
      <SectionWrapper sectionType="ncert-showcase" sections={sections} sectionsLoading={sectionsLoading}>
      <section className="py-8" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
        <div className="container">
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '3rem', 
            alignItems: 'center',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {/* Left Content */}
            <div style={{ padding: '2rem' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                marginBottom: '1rem',
                color: 'var(--muted-foreground)',
                fontSize: '0.9rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  background: 'var(--accent)', 
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '14px'
                }}>✓</div>
                ADHVA ACADEMY PRESENTS
              </div>
              
              <h2 style={{ 
                fontSize: 'clamp(2rem, 5vw, 3rem)', 
                fontWeight: 800,
                marginBottom: '1.5rem',
                color: 'var(--primary)',
                lineHeight: 1.2
              }}>
                NCERT Mastery Program
              </h2>
              
              <p style={{ 
                fontSize: '1.125rem', 
                color: 'var(--muted-foreground)', 
                marginBottom: '2rem',
                lineHeight: 1.7
              }}>
                Build a rock-solid foundation for your UPSC journey. Master core concepts and gain a competitive edge with our program.
              </p>
              
              <Link 
                to="/ncert-mastery" 
                className="btn-primary" 
                style={{ 
                  fontSize: '1rem', 
                  padding: '1rem 2rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: 600
                }}
              >
                Discover NCERT Mastery →
              </Link>
            </div>

            {/* Right Video Player */}
            <div style={{ 
              position: 'relative',
              paddingBottom: '56.25%', // 16:9 aspect ratio
              height: 0,
              overflow: 'hidden',
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
              background: '#000000'
            }}>
              <iframe 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                  borderRadius: '12px'
                }}
                src={
                  (() => {
                    const videoUrl = getNcertVideoContent('youtube_url') || "https://www.youtube.com/embed/OGklzsZpt9M";
                    // Extract video ID if it's a full embed URL
                    const videoId = videoUrl.includes('/embed/') 
                      ? videoUrl.split('/embed/')[1].split('?')[0]
                      : videoUrl.split('v=')[1]?.split('&')[0] || videoUrl;
                    
                    return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=1&rel=0&modestbranding=1`;
                  })()
                }
                title="NCERT Mastery Program Introduction"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </section>
      </SectionWrapper>

      {/* NCERT Mastery Test Series Poster Section */}
      <SectionWrapper sectionType="ncert-poster" sections={sections} sectionsLoading={sectionsLoading}>
      <section className="py-8" style={{ 
        background: 'linear-gradient(135deg, #FFF8F0 0%, #FFFFFF 100%)',
        padding: '5rem 0'
      }}>
        <div className="container">
          <div style={{ 
            maxWidth: '1200px', 
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '4rem',
            alignItems: 'center'
          }}>
            {/* Left Side - Poster Image */}
            <div style={{ 
              position: 'relative',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(255, 140, 56, 0.2)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 30px 80px rgba(255, 140, 56, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(255, 140, 56, 0.2)';
            }}>
              <img 
                src={getNcertPosterContent('poster_url') || "/images/ncert-mastery-poster.png"}
                alt="NCERT Mastery Test Series - UPSC Foundation" 
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  display: 'block',
                  borderRadius: '20px'
                }}
              />
              {/* Orange accent border */}
              <div style={{
                position: 'absolute',
                inset: '10px',
                border: '3px solid var(--accent)',
                borderRadius: '15px',
                pointerEvents: 'none',
                opacity: 0.5
              }}></div>
            </div>

            {/* Right Side - Content */}
            <div style={{ 
              padding: '3rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <h2 style={{ 
                fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', 
                marginBottom: '1.5rem', 
                color: '#1e3a5f',
                fontWeight: 800,
                lineHeight: 1.2
              }}>
                Unlock Your Potential!
              </h2>

              <p style={{
                fontSize: 'clamp(1.1rem, 2vw, 1.3rem)',
                color: '#6b8cae',
                lineHeight: 1.8,
                marginBottom: '3rem'
              }}>
                Discover our new NCERT Mastery program designed to build a strong foundation for your UPSC success. Comprehensive coverage, expert guidance, and proven strategies.
              </p>

              <button style={{
                padding: '1.2rem 3rem',
                fontSize: '1.2rem',
                fontWeight: 700,
                color: '#1e3a5f',
                background: '#f5a962',
                border: 'none',
                borderRadius: '50px',
                cursor: 'pointer',
                boxShadow: '0 10px 30px rgba(245, 169, 98, 0.4)',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.75rem',
                alignSelf: 'flex-start'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(245, 169, 98, 0.5)';
                e.currentTarget.style.background = '#ff9f4a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(245, 169, 98, 0.4)';
                e.currentTarget.style.background = '#f5a962';
              }}
              onClick={() => window.location.href = '/ncert-mastery'}>
                Learn More & Enroll
                <span style={{ fontSize: '1.4rem' }}>→</span>
              </button>
            </div>
          </div>

          {/* Responsive Styles */}
          <style jsx>{`
            @media (max-width: 768px) {
              .container > div {
                grid-template-columns: 1fr !important;
                gap: 2rem !important;
              }
            }
          `}</style>
        </div>
      </section>
      </SectionWrapper>

      {/* Guiding Principles of Adhva Academy */}
      <SectionWrapper sectionType="learning-paths" sections={sections} sectionsLoading={sectionsLoading}>
      <section className="py-8" style={{ background: '#f8f9fa' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{ 
              fontSize: 'clamp(2rem, 4vw, 2.5rem)', 
              marginBottom: '1rem', 
              color: 'var(--primary)',
              fontWeight: 700
            }}>
              Guiding Principles of Adhva Academy
            </h2>
            <p style={{ 
              fontSize: '1.125rem', 
              color: '#7C8BA1',
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: 1.7
            }}>
              Our commitment to your success is driven by a clear vision and a dedicated mission.
            </p>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '2rem',
            maxWidth: '1400px',
            margin: '0 auto'
          }}>
            {/* Our Vision */}
            <div 
              className="card guiding-principle-card" 
              style={{ 
                padding: '2.5rem 2rem',
                textAlign: 'center',
                border: '2px solid #E5E7EB',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(-5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                background: '#FFF3E6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                color: 'var(--accent)',
                fontSize: '2rem'
              }}>
                �
              </div>
              <h3 style={{ 
                marginBottom: '1.25rem', 
                fontSize: '1.5rem',
                color: 'var(--primary)',
                fontWeight: 700
              }}>
                Our Vision
              </h3>
              <p style={{ 
                color: '#7C8BA1', 
                fontSize: '1rem',
                lineHeight: 1.7,
                textAlign: 'center'
              }}>
                {getGuidingContent('vision') || 'To create a generation of civil servants who serve with integrity, empathy, and excellence—transforming society through ethical governance.'}
              </p>
            </div>

            {/* Our Mission */}
            <div 
              className="card guiding-principle-card" 
              style={{ 
                padding: '2.5rem 2rem',
                textAlign: 'center',
                border: '2px solid #E5E7EB',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(-5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                background: '#FFF3E6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                color: 'var(--accent)',
                fontSize: '2rem'
              }}>
                🎯
              </div>
              <h3 style={{ 
                marginBottom: '1.25rem', 
                fontSize: '1.5rem',
                color: 'var(--primary)',
                fontWeight: 700
              }}>
                Our Mission
              </h3>
              <p style={{ 
                color: '#7C8BA1', 
                fontSize: '1rem',
                lineHeight: 1.7,
                textAlign: 'center'
              }}>
                {getGuidingContent('mission') || 'To provide accessible, world-class mentorship and resources that empower aspirants to excel in civil service examinations with confidence and clarity.'}
              </p>
            </div>

            {/* Our Approach */}
            <div 
              className="card guiding-principle-card" 
              style={{ 
                padding: '2.5rem 2rem',
                textAlign: 'center',
                border: '2px solid #E5E7EB',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(-5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                background: '#FFF3E6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                color: 'var(--accent)',
                fontSize: '2rem'
              }}>
                💡
              </div>
              <h3 style={{ 
                marginBottom: '1.25rem', 
                fontSize: '1.5rem',
                color: 'var(--primary)',
                fontWeight: 700
              }}>
                Our Approach
              </h3>
              <p style={{ 
                color: '#7C8BA1', 
                fontSize: '1rem',
                lineHeight: 1.7,
                textAlign: 'center'
              }}>
                {getGuidingContent('approach') || 'We combine structured learning with personalized guidance, fostering both academic excellence and character development for holistic growth.'}
              </p>
            </div>
          </div>
        </div>

        {/* Responsive styles */}
        <style>{`
          @media (max-width: 1024px) {
            .guiding-principle-card {
              grid-column: span 1 !important;
            }
          }
          @media (max-width: 768px) {
            .guiding-principle-card {
              grid-column: span 3 !important;
            }
          }
        `}</style>
      </section>
      </SectionWrapper>

      {/* Learning Paths */}
      <SectionWrapper sectionType="learning-paths" sections={sections} sectionsLoading={sectionsLoading}>
      <section className="py-8" style={{ background: 'var(--secondary)' }}>
        <div className="container">
          <h2 className="text-center" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '1rem' }}>Learning Paths</h2>
          <p className="text-center" style={{ 
            color: '#7C8BA1', 
            fontSize: '1.15rem', 
            marginBottom: '3rem', 
            maxWidth: '700px', 
            margin: '0 auto 3rem',
            lineHeight: 1.6
          }}>
            Choose your journey to civil services success with our structured learning programs
          </p>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '2rem',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {[
              {
                id: 1,
                title: 'Adhva Prelims Path 2026',
                description: 'Complete UPSC Prelims test series with 45 comprehensive tests',
                details: 'Everything you need to clear Prelims in ONE complete program. Structured 4-round coverage with 27 GS tests, 18 CSAT tests, mentorship, and detailed analysis.',
                icon: '🎯',
                link: '/adhva-prelims-path-2026',
                features: ['45 Total Tests', '1-to-1 Mentorship', '4-Round Coverage', 'Eco Survey + Budget']
              },
              {
                id: 2,
                title: 'Adhva Diksoochi KAS 2026',
                description: 'Complete KPSC KAS Prelims program with 30 structured tests',
                details: 'Your compass to KAS success: foundation tests, consolidation rounds, full-length mocks, one-to-one mentorship and detailed performance insights.',
                icon: '🧭',
                link: '/adhva-diksoochi-kas-2026',
                features: ['30 Prelims Tests', '1-to-1 Mentorship', 'KPSC KAS Focus', 'Performance Reports']
              },
              {
                id: 3,
                title: 'Adhva Monthly Current Affairs',
                description: 'Stay updated with monthly current affairs tests and analysis',
                details: 'Master UPSC Current Affairs with our comprehensive monthly test series. Stop worrying and start testing! Stay ahead with the most relevant and up-to-date content.',
                icon: '📅',
                link: '/adhva-monthly-current-affairs',
                features: ['Monthly Tests', 'Detailed Analysis', 'Current Affairs Coverage', 'Expert Commentary']
              },
              {
                id: 4,
                title: 'NCERT Mastery',
                description: 'Complete NCERT coverage from Class 6 to 12 for UPSC preparation',
                details: 'Build a rock-solid foundation with comprehensive coverage of all NCERT textbooks. Perfect for beginners and those looking to strengthen their conceptual understanding.',
                icon: '📚',
                link: '/ncert-mastery',
                features: ['Class 6-12 Coverage', 'Video Lectures', 'Practice Questions', 'Expert Guidance']
              },
              {
                id: 5,
                title: 'Mentorship Program',
                description: 'Intensive preparation for UPSC Mains exam with answer writing practice',
                details: 'Get personalized guidance from experienced mentors and ex-civil servants. Master answer writing techniques and develop strategic exam approaches.',
                icon: '🏆',
                link: '/mentorship',
                features: ['Personal Mentorship', 'Answer Writing', 'Mock Tests', '8-Month Structured Plan']
              },
              {
                id: 6,
                title: 'Adhva Vibes - PSI',
                description: 'Complete roadmap to ace the Karnataka PSI exam',
                details: 'Your complete roadmap to ace the Karnataka PSI exam. From comprehensive test series to expert guidance, we\'ve designed everything you need to earn that uniform.',
                icon: '🚔',
                link: '/adhva-vibes-psi',
                features: ['37 Comprehensive Tests', 'Data-Driven Strategy', 'Expert Feedback', 'All-Karnataka Ranking']
              }
            ].map((program) => {
              return (
                <div 
                  key={program.id} 
                  data-testid={`program-${program.id}`} 
                  style={{ 
                    background: 'white',
                    position: 'relative',
                    border: 'none',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    borderRadius: '16px',
                    padding: '2rem',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Upcoming Badge */}
                  {program.isUpcoming && (
                    <div style={{
                      position: 'absolute',
                      top: '20px',
                      right: '-35px',
                      background: 'linear-gradient(135deg, var(--accent) 0%, #FFB570 100%)',
                      color: 'white',
                      padding: '0.5rem 3rem',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      transform: 'rotate(45deg)',
                      boxShadow: '0 4px 10px rgba(255, 140, 56, 0.3)',
                      letterSpacing: '0.5px',
                      zIndex: 10
                    }}>
                      UPCOMING
                    </div>
                  )}
                  
                  {/* Icon */}
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #FFE8D0 0%, #FFF3E6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.25rem',
                    fontSize: '28px'
                  }}>
                    {program.icon}
                  </div>
                  
                  {/* Title */}
                  <h3 style={{ 
                    fontSize: '1.5rem', 
                    marginBottom: '0.75rem', 
                    color: '#1a202c',
                    fontWeight: 700,
                    lineHeight: 1.3
                  }}>
                    {program.title}
                  </h3>
                  
                  {/* Description */}
                  <p style={{ 
                    color: '#8b92a7', 
                    fontSize: '0.95rem', 
                    lineHeight: 1.6, 
                    marginBottom: '0.75rem'
                  }}>
                    {program.description}
                  </p>
                  
                  {/* Additional Details */}
                  <p style={{ 
                    color: '#6b7280', 
                    fontSize: '0.875rem', 
                    lineHeight: 1.5, 
                    marginBottom: '1rem',
                    fontStyle: 'italic'
                  }}>
                    {program.details}
                  </p>
                  
                  {/* Features */}
                  {program.features && (
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '0.5rem', 
                      marginBottom: '1.5rem' 
                    }}>
                      {program.features.map((feature, idx) => (
                        <span 
                          key={idx}
                          style={{
                            fontSize: '0.75rem',
                            padding: '0.35rem 0.75rem',
                            background: '#f0f4f8',
                            color: '#4a5568',
                            borderRadius: '6px',
                            fontWeight: 500
                          }}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <Link 
                    to={program.link} 
                    style={{ 
                      width: '100%', 
                      textAlign: 'center', 
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '0.875rem 1.5rem',
                      background: program.isUpcoming ? '#6c7a89' : '#1e3a5f',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      textDecoration: 'none',
                      transition: 'all 0.3s ease',
                      cursor: program.isUpcoming ? 'not-allowed' : 'pointer',
                      opacity: program.isUpcoming ? 0.8 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!program.isUpcoming) {
                        e.currentTarget.style.background = '#2d4a6f';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!program.isUpcoming) {
                        e.currentTarget.style.background = '#1e3a5f';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                    onClick={(e) => {
                      if (program.isUpcoming) {
                        e.preventDefault();
                        alert('This program is coming soon! Stay tuned for updates.');
                      }
                    }}
                  >
                    {program.isUpcoming ? (
                      <>🔜 Coming Soon</>
                    ) : (
                      <>Explore Path <span style={{ fontSize: '1.1rem' }}>→</span></>
                    )}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      </SectionWrapper>

      {/* How It Works - 3-Phase Journey (Differentiators) */}
      <SectionWrapper sectionType="differentiators" sections={sections} sectionsLoading={sectionsLoading}>
      <section className="py-8">
        <div className="container">
          <h2 className="text-center" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '1rem' }}>How Our Mentorship Works</h2>
          <p className="text-center" style={{ color: 'var(--muted-foreground)', fontSize: '1.2rem', marginBottom: '3rem', maxWidth: '700px', margin: '0 auto 3rem' }}>
            A structured 8-month journey designed to take you from foundation to exam-ready
          </p>
          
          {/* 3-Phase Journey */}
          <div className="grid grid-3">
            <div className="card">
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem' }}>1</div>
              <h3 style={{ marginBottom: '0.75rem', color: 'var(--primary)' }}>Foundation Phase</h3>
              <p style={{ color: 'var(--muted-foreground)', marginBottom: '0.5rem' }}>Build strong basics with NCERT coverage and concept clarity</p>
              <p style={{ color: 'var(--accent)', fontWeight: 600, fontSize: '0.9rem' }}>Duration: 3 months</p>
            </div>
            <div className="card">
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem' }}>2</div>
              <h3 style={{ marginBottom: '0.75rem', color: 'var(--primary)' }}>Consolidation Phase</h3>
              <p style={{ color: 'var(--muted-foreground)', marginBottom: '0.5rem' }}>Practice with tests, current affairs integration, and revision</p>
              <p style={{ color: 'var(--accent)', fontWeight: 600, fontSize: '0.9rem' }}>Duration: 3 months</p>
            </div>
            <div className="card">
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem' }}>3</div>
              <h3 style={{ marginBottom: '0.75rem', color: 'var(--primary)' }}>Prelims Preparation</h3>
              <p style={{ color: 'var(--muted-foreground)', marginBottom: '0.5rem' }}>Full-length mocks, final revision, and exam strategy</p>
              <p style={{ color: 'var(--accent)', fontWeight: 600, fontSize: '0.9rem' }}>Duration: 2 months</p>
            </div>
          </div>

          <div className="text-center" style={{ marginTop: '2.5rem' }}>
            <Link to="/mentorship" className="btn-primary" style={{ fontSize: '1.1rem', padding: '0.75rem 2.5rem' }}>View Detailed Program →</Link>
          </div>
        </div>
      </section>
      </SectionWrapper>

      {/* What Makes Us Different - Features Section */}
      <SectionWrapper sectionType="mentorship" sections={sections} sectionsLoading={sectionsLoading}>
      <section className="py-8" style={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        padding: '5rem 0'
      }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <div style={{
              display: 'inline-block',
              padding: '0.5rem 1.5rem',
              background: 'linear-gradient(135deg, #FFE8D0 0%, #FFF3E6 100%)',
              borderRadius: '50px',
              marginBottom: '1rem'
            }}>
              <span style={{ 
                fontSize: '0.9rem', 
                fontWeight: 600,
                color: 'var(--accent)',
                letterSpacing: '0.5px'
              }}>
                OUR UNIQUE APPROACH
              </span>
            </div>
            <h2 style={{ 
              fontSize: 'clamp(2.2rem, 5vw, 3rem)', 
              marginBottom: '1rem', 
              color: 'var(--primary)',
              fontWeight: 800,
              lineHeight: 1.2
            }}>
              What Makes Us <span style={{ 
                background: 'linear-gradient(135deg, var(--accent) 0%, #FFB570 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Different</span>
            </h2>
            <p style={{
              fontSize: '1.1rem',
              color: '#6B7280',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: 1.6
            }}>
              Comprehensive support system designed to accelerate your UPSC journey
            </p>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', 
            gap: '2.5rem',
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 1rem'
          }}>
            {/* Personalized Mentorship */}
            <div 
              className="card" 
              onMouseEnter={() => setHoveredFeature('mentorship')}
              onMouseLeave={() => setHoveredFeature(null)}
              style={{ 
                padding: '2.5rem',
                background: 'white',
                border: hoveredFeature === 'mentorship' ? '3px solid var(--accent)' : '1px solid #E5E7EB',
                borderRadius: '16px',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: hoveredFeature === 'mentorship' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                boxShadow: hoveredFeature === 'mentorship' 
                  ? '0 20px 40px rgba(255, 140, 56, 0.25), 0 0 0 1px rgba(255, 140, 56, 0.1)' 
                  : '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.03)',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Subtle gradient overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '150px',
                height: '150px',
                background: 'radial-gradient(circle, rgba(255, 140, 56, 0.08) 0%, transparent 70%)',
                pointerEvents: 'none',
                opacity: hoveredFeature === 'mentorship' ? 1 : 0,
                transition: 'opacity 0.4s ease'
              }}></div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1.25rem',
                marginBottom: '1.75rem',
                position: 'relative'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: hoveredFeature === 'mentorship' 
                    ? 'linear-gradient(135deg, var(--accent) 0%, #FFB570 100%)'
                    : 'linear-gradient(135deg, #FFF3E6 0%, #FFE8D0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.75rem',
                  transition: 'all 0.4s ease',
                  boxShadow: hoveredFeature === 'mentorship' 
                    ? '0 8px 16px rgba(255, 140, 56, 0.3)' 
                    : '0 4px 8px rgba(255, 140, 56, 0.1)'
                }}>
                  <span style={{
                    filter: hoveredFeature === 'mentorship' ? 'brightness(0) invert(1)' : 'none',
                    transition: 'filter 0.4s ease'
                  }}>👥</span>
                </div>
                <h3 style={{ 
                  fontSize: '1.45rem',
                  color: hoveredFeature === 'mentorship' ? 'var(--accent)' : 'var(--primary)',
                  fontWeight: 700,
                  margin: 0,
                  transition: 'color 0.3s ease',
                  lineHeight: 1.3
                }}>
                  Personalized Mentorship
                </h3>
              </div>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0,
                margin: 0,
                position: 'relative'
              }}>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  One-on-one guidance from experienced UPSC mentors
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Regular progress monitoring and personalized feedback
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Strategic guidance tailored to your strengths and weaknesses
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Goal-setting and achievement tracking
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Motivation and emotional support throughout your journey
                </li>
              </ul>
            </div>

            {/* Doubt Solving Sessions */}
            <div 
              className="card"
              onMouseEnter={() => setHoveredFeature('doubt')}
              onMouseLeave={() => setHoveredFeature(null)}
              style={{ 
                padding: '2.5rem',
                background: 'white',
                border: hoveredFeature === 'doubt' ? '3px solid var(--accent)' : '1px solid #E5E7EB',
                borderRadius: '16px',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: hoveredFeature === 'doubt' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                boxShadow: hoveredFeature === 'doubt' 
                  ? '0 20px 40px rgba(255, 140, 56, 0.25), 0 0 0 1px rgba(255, 140, 56, 0.1)' 
                  : '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.03)',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Subtle gradient overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '150px',
                height: '150px',
                background: 'radial-gradient(circle, rgba(255, 140, 56, 0.08) 0%, transparent 70%)',
                pointerEvents: 'none',
                opacity: hoveredFeature === 'doubt' ? 1 : 0,
                transition: 'opacity 0.4s ease'
              }}></div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1.25rem',
                marginBottom: '1.75rem',
                position: 'relative'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: hoveredFeature === 'doubt' 
                    ? 'linear-gradient(135deg, var(--accent) 0%, #FFB570 100%)'
                    : 'linear-gradient(135deg, #FFF3E6 0%, #FFE8D0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.75rem',
                  transition: 'all 0.4s ease',
                  boxShadow: hoveredFeature === 'doubt' 
                    ? '0 8px 16px rgba(255, 140, 56, 0.3)' 
                    : '0 4px 8px rgba(255, 140, 56, 0.1)'
                }}>
                  <span style={{
                    filter: hoveredFeature === 'doubt' ? 'brightness(0) invert(1)' : 'none',
                    transition: 'filter 0.4s ease'
                  }}>💬</span>
                </div>
                <h3 style={{ 
                  fontSize: '1.45rem',
                  color: hoveredFeature === 'doubt' ? 'var(--accent)' : 'var(--primary)',
                  fontWeight: 700,
                  margin: 0,
                  transition: 'color 0.3s ease',
                  lineHeight: 1.3
                }}>
                  Doubt Solving Sessions
                </h3>
              </div>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0,
                margin: 0,
                position: 'relative'
              }}>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Weekly interactive doubt clearing sessions
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Small group format for personalized attention
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Facility to submit questions in advance
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Concept clarification by subject matter experts
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Supplementary study materials for difficult topics
                </li>
              </ul>
            </div>

            {/* Detailed Test Analysis */}
            <div 
              className="card"
              onMouseEnter={() => setHoveredFeature('analysis')}
              onMouseLeave={() => setHoveredFeature(null)}
              style={{ 
                padding: '2.5rem',
                background: 'white',
                border: hoveredFeature === 'analysis' ? '3px solid var(--accent)' : '1px solid #E5E7EB',
                borderRadius: '16px',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: hoveredFeature === 'analysis' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                boxShadow: hoveredFeature === 'analysis' 
                  ? '0 20px 40px rgba(255, 140, 56, 0.25), 0 0 0 1px rgba(255, 140, 56, 0.1)' 
                  : '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.03)',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Subtle gradient overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '150px',
                height: '150px',
                background: 'radial-gradient(circle, rgba(255, 140, 56, 0.08) 0%, transparent 70%)',
                pointerEvents: 'none',
                opacity: hoveredFeature === 'analysis' ? 1 : 0,
                transition: 'opacity 0.4s ease'
              }}></div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1.25rem',
                marginBottom: '1.75rem',
                position: 'relative'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: hoveredFeature === 'analysis' 
                    ? 'linear-gradient(135deg, var(--accent) 0%, #FFB570 100%)'
                    : 'linear-gradient(135deg, #FFF3E6 0%, #FFE8D0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.75rem',
                  transition: 'all 0.4s ease',
                  boxShadow: hoveredFeature === 'analysis' 
                    ? '0 8px 16px rgba(255, 140, 56, 0.3)' 
                    : '0 4px 8px rgba(255, 140, 56, 0.1)'
                }}>
                  <span style={{
                    filter: hoveredFeature === 'analysis' ? 'brightness(0) invert(1)' : 'none',
                    transition: 'filter 0.4s ease'
                  }}>📊</span>
                </div>
                <h3 style={{ 
                  fontSize: '1.45rem',
                  color: hoveredFeature === 'analysis' ? 'var(--accent)' : 'var(--primary)',
                  fontWeight: 700,
                  margin: 0,
                  transition: 'color 0.3s ease',
                  lineHeight: 1.3
                }}>
                  Detailed Test Analysis
                </h3>
              </div>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0,
                margin: 0,
                position: 'relative'
              }}>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Comprehensive performance analytics after each test
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Question-by-question review with faculty
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Identification of knowledge gaps and conceptual misunderstandings
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Personalized improvement strategies
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Comparative performance metrics to track progress
                </li>
              </ul>
            </div>

            {/* Notes Making Process */}
            <div 
              className="card"
              onMouseEnter={() => setHoveredFeature('notes')}
              onMouseLeave={() => setHoveredFeature(null)}
              style={{ 
                padding: '2.5rem',
                background: 'white',
                border: hoveredFeature === 'notes' ? '3px solid var(--accent)' : '1px solid #E5E7EB',
                borderRadius: '16px',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: hoveredFeature === 'notes' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                boxShadow: hoveredFeature === 'notes' 
                  ? '0 20px 40px rgba(255, 140, 56, 0.25), 0 0 0 1px rgba(255, 140, 56, 0.1)' 
                  : '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.03)',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Subtle gradient overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '150px',
                height: '150px',
                background: 'radial-gradient(circle, rgba(255, 140, 56, 0.08) 0%, transparent 70%)',
                pointerEvents: 'none',
                opacity: hoveredFeature === 'notes' ? 1 : 0,
                transition: 'opacity 0.4s ease'
              }}></div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1.25rem',
                marginBottom: '1.75rem',
                position: 'relative'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: hoveredFeature === 'notes' 
                    ? 'linear-gradient(135deg, var(--accent) 0%, #FFB570 100%)'
                    : 'linear-gradient(135deg, #FFF3E6 0%, #FFE8D0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.75rem',
                  transition: 'all 0.4s ease',
                  boxShadow: hoveredFeature === 'notes' 
                    ? '0 8px 16px rgba(255, 140, 56, 0.3)' 
                    : '0 4px 8px rgba(255, 140, 56, 0.1)'
                }}>
                  <span style={{
                    filter: hoveredFeature === 'notes' ? 'brightness(0) invert(1)' : 'none',
                    transition: 'filter 0.4s ease'
                  }}>📝</span>
                </div>
                <h3 style={{ 
                  fontSize: '1.45rem',
                  color: hoveredFeature === 'notes' ? 'var(--accent)' : 'var(--primary)',
                  fontWeight: 700,
                  margin: 0,
                  transition: 'color 0.3s ease',
                  lineHeight: 1.3
                }}>
                  Notes Making Process
                </h3>
              </div>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0,
                margin: 0,
                position: 'relative'
              }}>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Learn effective note-making techniques specific to UPSC
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Structured templates for different subjects
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Guidance on information organization and recall
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Mind-mapping and visual learning techniques
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Digital and traditional note-making methodologies
                </li>
              </ul>
            </div>

            {/* NCERT Gist */}
            <div 
              className="card"
              onMouseEnter={() => setHoveredFeature('ncert')}
              onMouseLeave={() => setHoveredFeature(null)}
              style={{ 
                padding: '2.5rem',
                background: 'white',
                border: hoveredFeature === 'ncert' ? '3px solid var(--accent)' : '1px solid #E5E7EB',
                borderRadius: '16px',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: hoveredFeature === 'ncert' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                boxShadow: hoveredFeature === 'ncert' 
                  ? '0 20px 40px rgba(255, 140, 56, 0.25), 0 0 0 1px rgba(255, 140, 56, 0.1)' 
                  : '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.03)',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Subtle gradient overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '150px',
                height: '150px',
                background: 'radial-gradient(circle, rgba(255, 140, 56, 0.08) 0%, transparent 70%)',
                pointerEvents: 'none',
                opacity: hoveredFeature === 'ncert' ? 1 : 0,
                transition: 'opacity 0.4s ease'
              }}></div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1.25rem',
                marginBottom: '1.75rem',
                position: 'relative'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: hoveredFeature === 'ncert' 
                    ? 'linear-gradient(135deg, var(--accent) 0%, #FFB570 100%)'
                    : 'linear-gradient(135deg, #FFF3E6 0%, #FFE8D0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.75rem',
                  transition: 'all 0.4s ease',
                  boxShadow: hoveredFeature === 'ncert' 
                    ? '0 8px 16px rgba(255, 140, 56, 0.3)' 
                    : '0 4px 8px rgba(255, 140, 56, 0.1)'
                }}>
                  <span style={{
                    filter: hoveredFeature === 'ncert' ? 'brightness(0) invert(1)' : 'none',
                    transition: 'filter 0.4s ease'
                  }}>📚</span>
                </div>
                <h3 style={{ 
                  fontSize: '1.45rem',
                  color: hoveredFeature === 'ncert' ? 'var(--accent)' : 'var(--primary)',
                  fontWeight: 700,
                  margin: 0,
                  transition: 'color 0.3s ease',
                  lineHeight: 1.3
                }}>
                  NCERT Gist
                </h3>
              </div>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0,
                margin: 0,
                position: 'relative'
              }}>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Techniques to extract Mains-relevant content from NCERTs
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Application of NCERT concepts to current affairs
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Strategies for integrating NCERT content into answer writing
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  marginBottom: '0.875rem',
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Development of multidimensional perspectives
                </li>
                <li style={{ 
                  color: '#4B5563', 
                  fontSize: '0.975rem',
                  lineHeight: 1.8,
                  paddingLeft: '1.75rem',
                  position: 'relative'
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0,
                    color: 'var(--accent)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>•</span>
                  Example answers incorporating NCERT foundations
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      </SectionWrapper>

      {/* Mission & Vision */}
      <SectionWrapper sectionType="mission-vision" sections={sections} sectionsLoading={sectionsLoading}>
      <section className="py-8" style={{ background: 'var(--secondary)' }}>
        <div className="container">
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', marginBottom: '1.5rem' }}>Our Mission</h2>
              <p style={{ fontSize: '1.125rem', color: 'var(--muted-foreground)', lineHeight: 1.8 }}>
                To provide accessible, high-quality education that empowers UPSC aspirants to achieve their dreams through innovative teaching methods and personalized mentorship.
              </p>
            </div>
            <div>
              <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', marginBottom: '1.5rem' }}>Our Vision</h2>
              <p style={{ fontSize: '1.125rem', color: 'var(--muted-foreground)', lineHeight: 1.8 }}>
                To be the most trusted and effective platform for UPSC preparation, creating a community of successful civil servants who serve society with integrity.
              </p>
            </div>
          </div>
        </div>
      </section>
      </SectionWrapper>



      {/* Quick Links */}
      <SectionWrapper sectionType="quick-links" sections={sections} sectionsLoading={sectionsLoading}>
      <section className="py-8">
        <div className="container text-center">
          <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '3rem' }}>Quick Access</h2>
          <div className="grid grid-3">
            <Link to="/resources" className="card" style={{ textDecoration: 'none', color: 'inherit', transition: 'all 0.3s ease' }} data-testid="quick-resources">
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem', color: 'var(--accent)', display: 'flex', justifyContent: 'center' }}><BookOpen size={48} /></div>
              <h3>Study Resources</h3>
            </Link>
            <Link to="/blog" className="card" style={{ textDecoration: 'none', color: 'inherit', transition: 'all 0.3s ease' }} data-testid="quick-blog">
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem', color: 'var(--accent)', display: 'flex', justifyContent: 'center' }}><Award size={48} /></div>
              <h3>Adhva Insight Blog</h3>
            </Link>
            <Link to="/videos" className="card" style={{ textDecoration: 'none', color: 'inherit', transition: 'all 0.3s ease' }} data-testid="quick-videos">
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem', color: 'var(--accent)', display: 'flex', justifyContent: 'center' }}><Users size={48} /></div>
              <h3>Video Library</h3>
            </Link>
          </div>
        </div>
      </section>
      </SectionWrapper>

      <Footer />
    </div>
  );
};

export default HomePage;
